import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/api';
import { Card, Btn, PageHeader } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const ALL_SECTIONS = [
  { id: 'experience',     label: 'Work Experience',     desc: 'Job history and professional roles' },
  { id: 'education',      label: 'Education',           desc: 'Degrees, institutions, and graduation years' },
  { id: 'skills',         label: 'Skills',              desc: 'Technical and soft skills' },
  { id: 'projects',       label: 'Projects',            desc: 'Personal or professional projects' },
  { id: 'strengths',      label: 'Key Strengths',       desc: 'Core competencies and strengths' },
  { id: 'goals',          label: 'Career Goals',        desc: 'Professional objectives and aspirations' },
  { id: 'hobbies',        label: 'Hobbies & Interests', desc: 'Personal interests outside of work' },
  { id: 'achievements',   label: 'Achievements',        desc: 'Notable accomplishments and milestones' },
  { id: 'certifications', label: 'Certifications',      desc: 'Professional certifications and licenses' },
  { id: 'awards',         label: 'Awards',              desc: 'Honors and recognitions received' },
  { id: 'languages',      label: 'Languages',           desc: 'Spoken or written languages and proficiency' },
  { id: 'volunteering',   label: 'Volunteering',        desc: 'Community service and volunteer work' },
  { id: 'publications',   label: 'Publications',        desc: 'Articles, papers, or books authored' },
  { id: 'references',     label: 'References',          desc: 'Professional references and contacts' },
  { id: 'customBlocks',   label: 'Custom Blocks',       desc: 'Freeform custom content blocks' },
  { id: 'jobInfo',        label: 'CL Job Information',  desc: 'Cover-letter company, role, and hiring manager details' },
  { id: 'content',        label: 'CL Letter Content',   desc: 'Cover-letter opening, body, and closing paragraphs' },
  { id: 'signature',      label: 'CL Signature',        desc: 'Cover-letter closing salutation and signature' },
];

const Toggle = ({ checked, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer', flexShrink: 0 }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
    <span style={{ position: 'absolute', inset: 0, background: checked ? '#F59E0B' : '#D1D5DB', borderRadius: 24, transition: 'background 0.2s' }} />
    <span style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18, background: 'white', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
  </label>
);

export const SectionsPage = () => {
  const [premiumSections, setPremiumSections] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState({ text: '', ok: true });

  useEffect(() => {
    adminApi.getSectionConfig()
      .then(d => setPremiumSections(d.premiumSections || []))
      .catch(e => setMsg({ text: e.message, ok: false }))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setPremiumSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
    setMsg({ text: '', ok: true });
  };

  const save = async () => {
    setSaving(true); setMsg({ text: '', ok: true });
    try {
      await adminApi.updateSectionConfig(premiumSections);
      setMsg({ text: '✅ Saved successfully!', ok: true });
      setTimeout(() => setMsg({ text: '', ok: true }), 3000);
    } catch (e) {
      setMsg({ text: `❌ ${e.message}`, ok: false });
    } finally { setSaving(false); }
  };

  const premiumCount = premiumSections.length;

  return (
    <div>
      <PageHeader
        title="Premium Sections"
        subtitle="Toggle which resume sections require a Pro subscription. Changes apply site-wide immediately after saving."
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {msg.text && <span style={{ fontSize: 13, color: msg.ok ? '#15803D' : '#DC2626' }}>{msg.text}</span>}
            <Btn color="blue" onClick={save} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Btn>
          </div>
        }
      />

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⭐</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#B45309' }}>{premiumCount}</div>
            <div style={{ fontSize: 11, color: '#B45309', fontWeight: 600 }}>Premium sections</div>
          </div>
        </div>
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🆓</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#15803D' }}>{ALL_SECTIONS.length - premiumCount}</div>
            <div style={{ fontSize: 11, color: '#15803D', fontWeight: 600 }}>Free sections</div>
          </div>
        </div>
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <RocketLoader size={140} message="Loading section config…" />
          </div>
        ) : (
          ALL_SECTIONS.map((sec, i) => {
            const isPremium = premiumSections.includes(sec.id);
            return (
              <div key={sec.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: i < ALL_SECTIONS.length - 1 ? '1px solid #E2E8F0' : 'none',
                background: isPremium ? '#FFFBEB' : 'white',
                transition: 'background 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>{sec.label}</span>
                      {isPremium && (
                        <span style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                          ⭐ Premium
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{sec.desc}</div>
                  </div>
                </div>
                <Toggle checked={isPremium} onChange={() => toggle(sec.id)} />
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
};
